import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import "./style.css";
import { VehicleMap } from "../../Components/Molecules"
import { VehiclesList } from "../../Components/Organisms";
import Loader from "../Loader";
import { LIVE_TRACKING_HEADERS } from "../../Library/tableHeaders";
import { StoreContext } from "../../Library/contexts";
import { AutoCompleteMultiSelect } from "../../Components/Atoms";
import { getAlerts, getStatesByCountryId } from "../../APIs";
import { LocationIcon } from "../../Assets/images/svgs";
import { GEOFENCE_TRIGGER_TYPE } from "../../Library/constants";
import { useToast } from "@chakra-ui/react";

const LiveTracking = () => {
  const [ showLoading, setShowLoading ] = useState(true);
  const [ vehicles, setVehicles ] = useState([]);
  const [ vids, setVids ] = useState([]);
  const [ vehiclesMap, setVehiclesMap ] = useState(new Map());
  const [ vehicleTableStats, setVehicleTableStats ] = useState(new Map());
  const [ highlightedVehicle, setHighlightedVehicle ] = useState({});
  const [ fetchedStates, setFetchedStates ] = useState([]);
  const [ showGeofences, setShowGeofences ] = useState(false);
  const [ geofencesToShow, setGeofencesToShow ] = useState([]);
  const [ isStateFilterActive, setIsStateFilterActive ] = useState(false);
  const [ statesWithUserVehs, setStatesWithUserVehs ] = useState([]);

  const originalVehicleData = useRef([]);
  const vehiclesCountCalculated = useRef(false);
  const Toast = useToast();
  const geofencesFetched = useRef(false);

  const { userVehicles, allStates, allGeofences, setAllGeofences } = useContext(StoreContext);

  const handleFetchedVehicles = (vehicles, dontResetVids) => {
    setVehicles(vehicles);
    if(!dontResetVids && vids.length != vehicles.length) setVids(vehicles.map((veh) => veh.idx));
    let vehMap = new Map();
    vehicles.forEach(vehicle => {
      vehMap.set(vehicle.idx, vehicle);
    });

    setVehiclesMap(vehMap);
    setShowLoading(false);
  }

  const handleVehicleHighlight = useCallback((vehicle) => {
    const updatedVehicles = vehicles.map(veh => ({
      ...veh,
      highlight: veh.idx === vehicle.vid,
    }));

    updatedVehicles.sort((a, b) => {
      if (a.highlight && !b.highlight) return -1;
      if (!a.highlight && b.highlight) return 1;
      return 0;
    });

    handleFetchedVehicles(updatedVehicles, true);
    setHighlightedVehicle(updatedVehicles.find(veh => veh.highlight));
  }, [vehicles]);


  useEffect(() => {
    handleFetchedVehicles(userVehicles);
  }, [ userVehicles ]);

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.VEHICLE_INFO_SHOWN, handleVehicleHighlight);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.VEHICLE_INFO_SHOWN, handleVehicleHighlight);
    }
  }, [handleVehicleHighlight]);

  const fetchAllStates = async () => {
    if (allStates && allStates.length) {
      setFetchedStates(allStates);
      return;
    }

    let states = await getStatesByCountryId(true);
    if (states && states.length) {
      states.forEach(state => {
        state.label = state.nm;
        state.value = state.idx;
      });

      setFetchedStates(states);
    }
  }

  useEffect(() => {
    fetchAllStates();
  }, []);

  useEffect(() => {
    if (!vehicles || !vehicles.length || !fetchedStates || !fetchedStates.length || vehiclesCountCalculated.current) return;
    let statesToShow = [];
    fetchedStates.forEach(state => {
      let stateHasVehicles = vehicles.filter(data => data.reg && data.reg.toLowerCase().startsWith(state.abb?.toLowerCase()));
      if (stateHasVehicles && stateHasVehicles.length) {
        state.label = `${state.nm} (${stateHasVehicles.length})`;
        statesToShow.push(state);
      }
    });

    setStatesWithUserVehs(statesToShow);
    vehiclesCountCalculated.current = true;
    setFetchedStates(statesToShow);

  }, [ vehicles, fetchedStates ]);

  const handleFilterApply = (selectedOptions, isSelectAll) => {
    if (!selectedOptions.length) return;

    if (!originalVehicleData.current || !originalVehicleData.current.length) originalVehicleData.current = vehicles;

    let filteredVehicles = [];
    if (isSelectAll) filteredVehicles = originalVehicleData.current;
    else {
      selectedOptions.forEach(option => {
        let filteredVehs = originalVehicleData.current.filter(data => {
          if (data && option && data.reg && option.abb) return data.reg.toLowerCase().startsWith(option.abb?.toLowerCase());
          return "";
        });
        filteredVehicles = filteredVehicles.concat(filteredVehs);
      });
    }

    if (!isStateFilterActive) setIsStateFilterActive(true);
    setVehicles(filteredVehicles);
    setVids(filteredVehicles.map(vehicle => vehicle.idx));
    let vehMap = new Map();
    filteredVehicles.forEach(vehicle => {
      vehMap.set(vehicle.idx, vehicle);
    });
    setVehiclesMap(vehMap);
  }

  const handleViewGeofences = (isChecked) => {
    if ((allGeofences && allGeofences.length) || geofencesFetched.current) setGeofencesToShow(allGeofences);
    else {
      getAlerts(GEOFENCE_TRIGGER_TYPE, (err, data) => {
        if(err) console.error("Error fetching geofence alerts", err);
        else {
          geofencesFetched.current = true;

          setAllGeofences(data);
          setGeofencesToShow(data);
          if (!data.length) Toast({ isClosable: true, description: "You haven't added any geofences yet", position: "bottom", variant: "subtle", duration: 3000 })
        }
      });
    }

    setShowGeofences(isChecked);
  }

  return (
    <>
      {!showLoading ? <section className="panel-main live-tracking">
        <header className="live-tracking-header">
          <div className="select-state-dropdown">
            {statesWithUserVehs && statesWithUserVehs.length > 1 ?  <AutoCompleteMultiSelect autoFillOptions={fetchedStates} options={fetchedStates} placeholder={"All States"} icon={<LocationIcon />} handleFilterApply={handleFilterApply} showListControls={true} /> : null}
          </div>
          {/* <Button leftIcon={<AddModelIcon />} className="add-btn" onClick={() => navigate(PATHS.ADDVEHICLE)}>
            Add New Vehicle
          </Button> */}
        </header>
        <div className="lt-map">
          {(vids && vids.length) ? <VehicleMap isStateFilterActive={isStateFilterActive} showExistingGeofences={showGeofences} setShowExistingGeofences={handleViewGeofences} hideGeofenceMarkers={true} vids={vids} vehMap={vehiclesMap} allGeofences={geofencesToShow} updateVehiclesTable={(vehicleStats) => setVehicleTableStats(vehicleStats)} showExistingGfsToggle={true} /> : <p className="no-map-message">There are no vehicles available</p>}
        </div>

        <VehiclesList tableHeaders={LIVE_TRACKING_HEADERS} allVehicles={vehicles} vehicleStats={vehicleTableStats} highlightedVehicle={highlightedVehicle} />
      </section> : <Loader hide={true} />}
    </>
  )
}

export default LiveTracking
